import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiUploadCloud, FiX, FiPlus } from "react-icons/fi";
import api, { imgUrl } from "../utils/api";
import Loader from "../components/Loader";

const CATEGORIES = ["Puja Samagri", "Gemstones", "Rudraksha", "Yantra", "Idols & Murti", "Books", "Astrology Services", "Other"];

const emptyForm = {
  name: "",
  category: "Puja Samagri",
  price: "",
  discountPrice: "",
  images: [],
  shortDescription: "",
  description: "",
  specifications: [],
  inStock: true,
  featured: false,
};

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/products/admin/edit/${id}`)
        .then((res) => setForm(res.data))
        .catch(() => toast.error("Failed to load product"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("images", f));
    try {
      const { data } = await api.post("/upload/multiple", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, images: [...form.images, ...data.urls] });
      toast.success("Images uploaded");
    } catch (err) {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });
  };

  const addSpec = () => {
    setForm({ ...form, specifications: [...form.specifications, { key: "", value: "" }] });
  };

  const updateSpec = (idx, field, value) => {
    const specs = [...form.specifications];
    specs[idx][field] = value;
    setForm({ ...form, specifications: specs });
  };

  const removeSpec = (idx) => {
    setForm({ ...form, specifications: form.specifications.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.shortDescription || !form.description) {
      toast.error("Name, price, short description and description are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      discountPrice: Number(form.discountPrice) || 0,
    };
    try {
      if (isEdit) {
        await api.put(`/products/${id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      navigate("/admin/products");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-3xl">
      <Link to="/admin/products" className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all">
        <FiArrowLeft /> Back to Products
      </Link>

      <h1 className="font-heading text-2xl text-maroon-800 mb-6">{isEdit ? "Edit Product" : "Add New Product"}</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="e.g. 5 Mukhi Rudraksha Mala"
          />
        </div>

        <div className="grid sm:grid-cols-3 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select name="category" value={form.category} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-white">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) *</label>
            <input type="number" name="price" value={form.price} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Discount Price (₹)</label>
            <input type="number" name="discountPrice" value={form.discountPrice} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg border border-gray-300" placeholder="Optional" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Product Images</label>
          <div className="flex flex-wrap gap-3 mb-3">
            {form.images.map((img, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                <img src={imgUrl(img)} alt={`product ${idx}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(idx)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5">
                  <FiX size={12} />
                </button>
              </div>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
            <FiUploadCloud /> {uploading ? "Uploading..." : "Upload Images (up to 6)"}
            <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Short Description * (max 250 chars, shown on listing)</label>
          <textarea
            name="shortDescription"
            value={form.shortDescription}
            onChange={handleChange}
            rows={2}
            maxLength={250}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Description *</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={5}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Specifications</label>
          <div className="space-y-2">
            {form.specifications.map((spec, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  type="text"
                  placeholder="Key (e.g. Material)"
                  value={spec.key}
                  onChange={(e) => updateSpec(idx, "key", e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm"
                />
                <input
                  type="text"
                  placeholder="Value (e.g. Pure Copper)"
                  value={spec.value}
                  onChange={(e) => updateSpec(idx, "value", e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm"
                />
                <button type="button" onClick={() => removeSpec(idx)} className="px-3 text-red-500 hover:bg-red-50 rounded-lg">
                  <FiX />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addSpec} className="mt-2 inline-flex items-center gap-1 text-sm text-saffron-600 font-medium">
            <FiPlus /> Add Specification
          </button>
        </div>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" name="inStock" checked={form.inStock} onChange={handleChange} className="w-4 h-4" />
            In Stock
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="w-4 h-4" />
            Featured (shown on homepage)
          </label>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-maroon-700 hover:bg-maroon-800 disabled:opacity-60 text-gold-100 px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          <FiSave /> {saving ? "Saving..." : "Save Product"}
        </button>
      </form>
    </div>
  );
}
